import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const LurganChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Lurgan | Fibromyalgia Hypnotherapy County Armagh</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Lurgan from Belfast clinic (27 miles). Neuroscience approach for fibromyalgia, back pain, arthritis. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Lurgan, fibromyalgia hypnotherapy Lurgan, back pain treatment Lurgan, arthritis pain Lurgan, pain management County Armagh" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/lurgan-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Lurgan | Fibromyalgia Hypnotherapy County Armagh" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Lurgan. Neuroscience approach for fibromyalgia, back pain, arthritis." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Lurgan",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Lurgan and County Armagh from Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, migraines, and neuropathic pain with NHS-complementary approach. Supporting working-class community with 15 years nursing experience.",
            "url": "https://www.sharonclare-hypnotherapy.com/lurgan-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Lurgan",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Lurgan from Belfast clinic?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 27 miles from Lurgan via the M1 motorway from Junction 10, taking 35-40 minutes by car in normal traffic. This manageable distance makes regular pain management appointments practical. Many Lurgan clients find the drive worthwhile for specialist care, and the journey can be therapeutic - time to transition into a healing mindset away from daily stresses."
                }
              },
              {
                "@type": "Question",
                "name": "Can chronic pain treatment be done online?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Lurgan residents who prefer online treatment or cannot make the drive to Belfast during pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, or anyone who wants maximum flexibility."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for fibromyalgia?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy can be highly effective for fibromyalgia management. Fibromyalgia involves central sensitization where the nervous system becomes hypersensitive and amplifies pain signals. Hypnotherapy works by calming this overactive alarm system, reducing stress that worsens fibromyalgia symptoms, improving sleep quality (essential for fibromyalgia management), and helping your brain develop healthier pain processing patterns. Many Lurgan clients with fibromyalgia report significant improvements in pain levels, fatigue, and daily functioning."
                }
              },
              {
                "@type": "Question",
                "name": "Can I continue NHS treatment while doing hypnotherapy?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Southern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Lurgan clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments."
                }
              },
              {
                "@type": "Question",
                "name": "How does working-class manual labor affect chronic pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Manual labor in retail, warehousing, or light manufacturing creates repetitive strain injuries, chronic back pain from lifting and standing, and joint pain from repetitive movements. When you combine physical strain with economic stress, job insecurity, and limited control over working conditions, the nervous system stays on high alert, which significantly amplifies pain signals. Many Lurgan workers in these sectors experience pain that feels worse during stressful periods because stress directly affects pain perception. Hypnotherapy addresses both the physical pain processing and the stress that intensifies it."
                }
              },
              {
                "@type": "Question",
                "name": "Is pain management confidential?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. All hypnotherapy sessions are completely confidential and bound by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Nothing you share in sessions will be discussed with anyone else without your explicit permission (except in extremely rare circumstances where there's a risk of serious harm). Many Lurgan clients appreciate being able to discuss their pain and the stress affecting it in a completely private, non-judgmental space away from their community."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Lurgan
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Lurgan from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">27 miles</span> from Lurgan via M1 • <span className="font-semibold">35-40 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Online sessions available for Lurgan residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Lurgan
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Lurgan and County Armagh from our Belfast clinic, located approximately 27 miles northeast via the M1 motorway from Junction 10. The journey takes 35-40 minutes, making specialist chronic pain support accessible for this historic market town community. For Lurgan residents dealing with persistent pain conditions like fibromyalgia, chronic back pain, or arthritis, this manageable distance provides access to evidence-based hypnotherapy that works alongside your NHS care.
              </p>
              <p>
                Living with chronic pain in Lurgan comes with specific pressures that affect how pain feels. This is a working-class community with strong values and practical attitudes to health, where many residents work in retail at the Outlet, in local manufacturing and warehousing, or in service sector roles. These jobs often involve standing all day, repetitive movements, heavy lifting, or customer-facing stress. When you combine the physical strain of manual labor with economic worries, shift work patterns, and the pressure of making ends meet, your nervous system stays on high alert, and that amplifies pain signals significantly.
              </p>
              <p>
                Modern pain neuroscience shows us that chronic pain is processed in the nervous system, not just at the injury site. When we're stressed from work pressures, financial concerns, or family responsibilities, our brain's alarm system becomes hypersensitive and turns up the volume on pain signals. This is why the same injury can feel worse on a stressful day, or why pain persists long after tissue damage has healed. This is where solution-focused hypnotherapy excels. By calming your overactive nervous system, teaching your brain healthier pain processing patterns, and addressing the stress that intensifies pain, many Lurgan clients experience significant reductions in pain levels and improvements in daily functioning.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I bring medical understanding to chronic pain treatment. I understand conditions like fibromyalgia, chronic back pain, and arthritis from both a clinical and therapeutic perspective. I know how frustrating it is when you're told "the scan looks fine" or "you'll just have to live with it" while you're struggling with real, debilitating pain every day. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you regain control, reduce pain intensity, and improve quality of life. This isn't about pretending the pain doesn't exist - it's about retraining your nervous system to respond differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. This is why pain can feel disproportionate to actual tissue damage, and why work stress or financial pressure can worsen pain intensity. Your nervous system's alarm is stuck on high volume.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from demanding physical work, economic uncertainty, or family pressures keeps your survival system on high alert. When your brain perceives threat - even from bills or job insecurity - it keeps pain signals turned up as a protective mechanism. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Neuroplasticity</h3>
                <p className="text-gray-700">
                  The brain learns pain pathways through repetition. The more they're used, the easier they fire - like a well-worn path through grass. But neuroplasticity also means we can create new, healthier pathways. Your brain can learn to process pain differently, and hypnotherapy facilitates that relearning.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well. For Lurgan workers dealing with the physical demands of retail, warehousing, or manual labor combined with economic stress, this overprotective system often gets stuck in overdrive. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for Lurgan residents dealing with the physical demands of manual labor, economic stress, and the accumulated tension of working-class life where financial security often feels precarious.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways. Neuroscience research shows the brain can literally turn down the volume on pain signals when it feels safe.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Harness Neuroplasticity</h3>
                    <p className="text-gray-700">
                      Creates new, healthier neural pathways so your brain can learn to interpret sensations differently. The brain's remarkable ability to change means chronic pain doesn't have to be permanent. We can retrain your nervous system to respond to signals in a healthier way.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use at work, at home, or during your commute to manage pain flare-ups, reduce stress, and improve sleep - all factors that significantly influence pain levels. These are practical tools for real life, not just the therapy room.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. Studies show hypnotherapy can significantly reduce pain intensity for conditions like fibromyalgia, chronic back pain, and arthritis by working with the brain's natural pain control mechanisms. A major review found hypnotherapy 68% more effective than no treatment for chronic pain conditions.
              </p>
              <p className="text-gray-700 text-lg">
                For Lurgan residents waiting months for Southern Trust pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving. The 35-40 minute drive to Belfast is manageable for the potential to significantly reduce your pain and improve your quality of life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sharon's Approach */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Southern Trust pain clinic waiting times often exceeding 12 months, many Lurgan residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge, offering relief and improved functioning while you wait for or work alongside NHS care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy as part of a multidisciplinary approach alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. This medical background means I understand when something needs medical attention versus when hypnotherapy can help, and I'll always support you in getting appropriate medical care.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Lurgan
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M1 motorway northbound from Junction 10 (near Lurgan) into Belfast, then south along the Ormeau Road
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 27 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 35-40 minutes in normal traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Manageable Journey</h3>
                <p className="text-gray-700">
                  The M1 motorway provides direct, straightforward access from Lurgan to Belfast. At 27 miles, this is a manageable distance for regular pain management appointments. Many clients find the journey worthwhile for specialist care that can significantly reduce their pain levels, and some even share that the drive becomes therapeutic - dedicated time away from daily pressures to focus on your healing and wellbeing.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                If traveling is difficult due to your pain condition, mobility limitations, or during severe flare-ups, I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Lurgan. This option is ideal for those experiencing high pain days, anyone with mobility concerns, or those who prefer the flexibility of online therapy. You can switch between in-person and online sessions based on your needs and pain levels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What County Armagh Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My chronic back pain from years of working in retail and standing all day was unbearable. The medication helped a bit but I was still struggling. Sharon's hypnotherapy helped me understand how stress was making the pain worse. After 8 sessions, I've been able to reduce my painkillers and the pain levels are much more manageable. I can actually get through a shift without agony now. Her nursing background meant she really understood what I was dealing with."
                </p>
                <p className="font-semibold text-gray-900">J.M., County Armagh - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in the warehouse, my repetitive strain injuries and shoulder pain were getting worse every year. I was worried I'd have to quit my job. Sharon's approach helped calm my nervous system and the pain levels dropped significantly. I'm not pain-free but it's manageable now, and I've learned techniques I can use during work when pain flares up. The drive to Belfast was nothing compared to the improvement in my quality of life."
                </p>
                <p className="font-semibold text-gray-900">P.D., Craigavon Borough - Repetitive Strain Injury</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia for 6 years, I'd tried everything the NHS offered. The hypnotherapy with Sharon made a real difference - she helped me manage my pain in ways the medical system couldn't. I'm sleeping better, my pain flare-ups are less frequent and less severe, and I feel like I have some control back. The online sessions were perfect because on bad pain days I couldn't have made the drive. Can't recommend her enough."
                </p>
                <p className="font-semibold text-gray-900">S.H., County Armagh - Fibromyalgia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Lurgan Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Lurgan from Belfast clinic?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 27 miles from Lurgan via the M1 motorway from Junction 10, taking 35-40 minutes by car in normal traffic. This manageable distance makes regular pain management appointments practical. Many Lurgan clients find the drive worthwhile for specialist care, and the journey can be therapeutic - time to transition into a healing mindset away from daily stresses.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can chronic pain treatment be done online?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Lurgan residents who prefer online treatment or cannot make the drive to Belfast during pain flare-ups. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions. This option is ideal for clients with mobility limitations, those experiencing severe pain days, or anyone who wants maximum flexibility.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for fibromyalgia?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy can be highly effective for fibromyalgia management. Fibromyalgia involves central sensitization where the nervous system becomes hypersensitive and amplifies pain signals. Hypnotherapy works by calming this overactive alarm system, reducing stress that worsens fibromyalgia symptoms, improving sleep quality (essential for fibromyalgia management), and helping your brain develop healthier pain processing patterns. Many Lurgan clients with fibromyalgia report significant improvements in pain levels, fatigue, and daily functioning.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can I continue NHS treatment while doing hypnotherapy?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Southern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Lurgan clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does working-class manual labor affect chronic pain?
                </h3>
                <p className="text-gray-700">
                  Manual labor in retail, warehousing, or light manufacturing creates repetitive strain injuries, chronic back pain from lifting and standing, and joint pain from repetitive movements. When you combine physical strain with economic stress, job insecurity, and limited control over working conditions, the nervous system stays on high alert, which significantly amplifies pain signals. Many Lurgan workers in these sectors experience pain that feels worse during stressful periods because stress directly affects pain perception. Hypnotherapy addresses both the physical pain processing and the stress that intensifies it.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is pain management confidential?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. All hypnotherapy sessions are completely confidential and bound by professional ethics standards. As a CNHC registered hypnotherapist, I maintain strict confidentiality. Nothing you share in sessions will be discussed with anyone else without your explicit permission (except in extremely rare circumstances where there's a risk of serious harm). Many Lurgan clients appreciate being able to discuss their pain and the stress affecting it in a completely private, non-judgmental space away from their community.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LurganChronicPain;
